// src/app/pages/campaigns/Schedule.jsx
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Tabs from "./Tabs";
import { useParams } from "react-router-dom";
import axios from "../../lib/axios";
import { Calendar, Clock, Save, Globe, RefreshCw } from "lucide-react";
import "../Pages.css";

const TIMEZONE_OFFSETS = {
  "UTC": 0, "EST": -300, "EDT": -240, "CST": -360, "CDT": -300,
  "MST": -420, "MDT": -360, "PST": -480, "PDT": -420,
  "IST": 330, "GMT": 0, "CET": 60, "CEST": 120,
  "JST": 540, "AEST": 600, "AEDT": 660,
};

const convertToUTC = (timeStr, timezone) => {
  if (!timeStr) return null;
  const offset = TIMEZONE_OFFSETS[timezone] || 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  date.setMinutes(date.getMinutes() - offset);
  return `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2,'0')}`;
};

const convertFromUTC = (timeStr, timezone) => {
  if (!timeStr) return "";
  const offset = TIMEZONE_OFFSETS[timezone] || 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  date.setMinutes(date.getMinutes() + offset);
  return `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2,'0')}`;
};

export default function Schedule() {
  const { id } = useParams();

  const [scheduleName, setScheduleName]   = useState("");
  const [startDate, setStartDate]         = useState(null);
  const [endDate, setEndDate]             = useState(null);
  const [fromTime, setFromTime]           = useState("");
  const [toTime, setToTime]               = useState("");
  const [timezone, setTimezone]           = useState("UTC");
  const [selectedDays, setSelectedDays]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [originalFromTimeUTC, setOriginalFromTimeUTC] = useState("");
  const [originalToTimeUTC, setOriginalToTimeUTC]     = useState("");

  const days      = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const timezones = [
    { value:"UTC",  label:"UTC — Coordinated Universal Time" },
    { value:"EST",  label:"EST — Eastern Standard (US)" },
    { value:"EDT",  label:"EDT — Eastern Daylight (US)" },
    { value:"CST",  label:"CST — Central Standard (US)" },
    { value:"CDT",  label:"CDT — Central Daylight (US)" },
    { value:"MST",  label:"MST — Mountain Standard (US)" },
    { value:"MDT",  label:"MDT — Mountain Daylight (US)" },
    { value:"PST",  label:"PST — Pacific Standard (US)" },
    { value:"PDT",  label:"PDT — Pacific Daylight (US)" },
    { value:"IST",  label:"IST — India Standard Time" },
    { value:"GMT",  label:"GMT — Greenwich Mean Time" },
    { value:"CET",  label:"CET — Central European Time" },
    { value:"CEST", label:"CEST — Central European Summer" },
    { value:"JST",  label:"JST — Japan Standard Time" },
    { value:"AEST", label:"AEST — Australian Eastern Standard" },
    { value:"AEDT", label:"AEDT — Australian Eastern Daylight" },
  ];

  const toggleDay = (day) =>
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    const fetch = async () => {
      try {
        const { data } = await axios.get(`email-campaign/get-schedule/${id}`);
        if (data) {
          setScheduleName(data.schedule_name ?? "");
          setStartDate(data.start_date ? new Date(data.start_date) : null);
          setEndDate(data.end_date ? new Date(data.end_date) : null);
          setOriginalFromTimeUTC(data.from_time ?? "");
          setOriginalToTimeUTC(data.to_time ?? "");
          const tz = data.timezone ?? "UTC";
          setTimezone(tz);
          setFromTime(convertFromUTC(data.from_time, tz));
          setToTime(convertFromUTC(data.to_time, tz));
          setSelectedDays(Array.isArray(data.days) ? data.days : []);
        }
      } catch { /* no existing schedule */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const handleTimezoneChange = (tz) => {
    if (originalFromTimeUTC || originalToTimeUTC) {
      setFromTime(convertFromUTC(originalFromTimeUTC, tz));
      setToTime(convertFromUTC(originalToTimeUTC, tz));
    }
    setTimezone(tz);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const utcFrom = convertToUTC(fromTime, timezone);
      const utcTo   = convertToUTC(toTime, timezone);
      await axios.post(`email-campaign/schedule/${id}`, {
        campaign_id:   id,
        schedule_name: scheduleName,
        start_date:    startDate ? startDate.toISOString().split("T")[0] : null,
        end_date:      endDate   ? endDate.toISOString().split("T")[0]   : null,
        from_time:     utcFrom,
        to_time:       utcTo,
        timezone,
        days:          selectedDays,
      });
      setOriginalFromTimeUTC(utcFrom);
      setOriginalToTimeUTC(utcTo);
      alert("Schedule saved successfully!");
    } catch { alert("Failed to save schedule."); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="page animate-fade-in">
      <Tabs activeTab="Schedule" campaignId={id} />
      <div className="card" style={{ padding: '4rem', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div className="loader" />
      </div>
    </div>
  );

  const hasSummary = fromTime || toTime || selectedDays.length > 0;

  return (
    <div className="page animate-fade-in">
      <Tabs activeTab="Schedule" campaignId={id} />

      <div className="page-header">
        <div className="header-info">
          <h2>Campaign Schedule</h2>
          <p>Configure when your emails should be sent</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn btn-primary">
          {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
          Save Schedule
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><Calendar size={17} /> Schedule Configuration</h3>
        </div>
        <div className="card-body">

          {/* Schedule Name */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label>Schedule Name</label>
            <input
              type="text"
              value={scheduleName}
              onChange={e => setScheduleName(e.target.value)}
              placeholder="e.g., Weekday Morning Send"
            />
          </div>

          {/* Date Range */}
          <div className="form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label>Start Date</label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select start date"
                isClearable
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select end date"
                isClearable
              />
            </div>
          </div>

          {/* Timezone */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
              <Globe size={13} /> Timezone
            </label>
            <select value={timezone} onChange={e => handleTimezoneChange(e.target.value)}>
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <span className="time-hint">Times are stored in UTC and displayed in your selected timezone.</span>
          </div>

          {/* Time Window */}
          <div className="form-row" style={{ marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
                <Clock size={13} /> From Time ({timezone})
              </label>
              <input type="time" value={fromTime} onChange={e => setFromTime(e.target.value)} />
              {fromTime && (
                <span className="time-hint">
                  <Globe size={11} /> UTC: {convertToUTC(fromTime, timezone)}
                </span>
              )}
            </div>
            <div className="form-group">
              <label style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
                <Clock size={13} /> To Time ({timezone})
              </label>
              <input type="time" value={toTime} onChange={e => setToTime(e.target.value)} />
              {toTime && (
                <span className="time-hint">
                  <Globe size={11} /> UTC: {convertToUTC(toTime, timezone)}
                </span>
              )}
            </div>
          </div>

          {/* Active Days */}
          <div style={{ marginBottom: hasSummary ? '0' : '0.25rem' }}>
            <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--color-text-secondary)', marginBottom:'0.75rem', letterSpacing:'0.01em' }}>
              Active Days
            </label>
            <div className="day-toggle-grid">
              {days.map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`day-toggle-btn${selectedDays.includes(day) ? ' active' : ''}`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Schedule Summary */}
          {hasSummary && (
            <div className="schedule-summary">
              <h4>Summary</h4>
              <p>
                {selectedDays.length > 0 && (
                  <><strong>Days:</strong> {selectedDays.join(', ')}<br /></>
                )}
                {fromTime && toTime && (
                  <><strong>Window:</strong> {fromTime} – {toTime} ({timezone})
                    &nbsp;·&nbsp;
                    <strong>UTC:</strong> {convertToUTC(fromTime, timezone)} – {convertToUTC(toTime, timezone)}
                  </>
                )}
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
